import { authState, updateProfile, createGroup, joinGroup } from '../auth.js';
import { store, getCourseStats, fetchUserProgressFromSupabase } from '../store.js';
import { openAuthModal } from './AuthModal.js';
import { showToast } from '../utils/toast.js';

let activeDashTab = 'reflections';

export function renderDashboard(container) {
  const user = authState.user;
  const profile = authState.profile;
  const stats = getCourseStats();

  if (!user) {
    container.innerHTML = `
      <div class="content-container" style="text-align:center; max-width:600px; padding:5rem 1.5rem;">
        <div class="brand-icon-box" style="margin:0 auto 1.5rem; width:56px; height:56px; font-size:1.6rem;">
          <i class="fa-solid fa-lock"></i>
        </div>
        <h2 class="hero-title" style="font-size:2.2rem; margin-bottom:1rem;">Student Sign In Required</h2>
        <p style="color:var(--text-secondary); margin-bottom:2rem; font-size:1.05rem;">
          To view your personal discipleship dashboard, track lesson progress across devices, and share reflections with your small group cohort, please sign in or register.
        </p>
        <button id="dash-signin-btn" class="btn-primary" style="margin:0 auto; padding:0.8rem 2rem; font-size:1rem;">
          <i class="fa-solid fa-user-plus"></i> Sign In / Create Account
        </button>
      </div>
    `;
    const btn = container.querySelector('#dash-signin-btn');
    if (btn) btn.onclick = () => openAuthModal('signin');
    return;
  }

  const displayName = profile?.display_name || user?.user_metadata?.display_name || 'Disciple';
  const cohortName = profile?.cohort_name || 'General Cohort';

  const myReflections = Object.entries(store.reflections).map(([lid, r]) => {
    const lesson = store.lessons.find(l => l.id === lid);
    return { lesson, ...r };
  }).filter(r => r.lesson && r.response_text);

  container.innerHTML = `
    <div class="content-container">
      <!-- PROFILE HEADER -->
      <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-xl); padding:2rem; backdrop-filter:blur(12px); margin-bottom:1.5rem; display:flex; align-items:center; justify-content:space-between; gap:1.5rem; flex-wrap:wrap;">
        <div style="display:flex; align-items:center; gap:1.25rem;">
          <div class="avatar-circle" style="width:64px; height:64px; font-size:1.8rem;">
            ${displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style="font-size:0.8rem; font-weight:700; color:var(--accent-blue); text-transform:uppercase; letter-spacing:0.05em;">
              Student Profile
            </div>
            <h1 style="font-family:var(--font-display); font-size:1.8rem; font-weight:700;">
              ${displayName}
            </h1>
            <div style="font-size:0.9rem; color:var(--text-secondary); display:flex; align-items:center; gap:0.5rem; margin-top:0.2rem; flex-wrap:wrap;">
              <span><i class="fa-solid fa-users"></i> Cohort: <strong>${cohortName}</strong></span>
              <span>•</span>
              <span><i class="fa-solid fa-envelope"></i> ${user.email}</span>
            </div>
          </div>
        </div>

        <div style="display:flex; gap:0.75rem; flex-wrap:wrap;">
          <button id="group-action-btn" class="btn-primary" style="font-size:0.85rem; padding:0.5rem 1rem;">
            <i class="fa-solid fa-people-roof"></i> ${store.currentGroup ? 'Group Details & Invite' : 'Join / Create Group'}
          </button>
          <button id="edit-profile-btn" class="btn-secondary" style="font-size:0.85rem; padding:0.5rem 1rem;">
            <i class="fa-solid fa-pen-to-square"></i> Edit Profile
          </button>
        </div>
      </div>

      <!-- ACTIVE GROUP BANNER (If in a group) -->
      ${store.currentGroup ? `
        <div style="background:linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(99, 102, 241, 0.08) 100%); border:1px solid rgba(59, 130, 246, 0.25); border-radius:var(--radius-xl); padding:1.25rem 1.5rem; margin-bottom:2rem; display:flex; align-items:center; justify-content:space-between; gap:1.25rem; flex-wrap:wrap;">
          <div>
            <div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:0.25rem;">
              <span class="badge" style="background:var(--accent-blue); color:#fff; font-size:0.75rem; padding:0.2rem 0.6rem; border-radius:var(--radius-full); font-weight:600;">
                <i class="fa-solid fa-users"></i> ${store.currentGroup.my_role === 'leader' ? 'Leader' : 'Member'}
              </span>
              <h3 style="font-size:1.15rem; font-weight:700; margin:0;">${store.currentGroup.name}</h3>
            </div>
            <p style="font-size:0.85rem; color:var(--text-secondary); margin:0;">
              Invite friends to this group so your progress, journal reflections, and discussions are linked together.
            </p>
          </div>

          <div style="display:flex; align-items:center; gap:0.6rem; background:rgba(0,0,0,0.25); padding:0.4rem 0.75rem; border-radius:var(--radius-md); border:1px solid var(--border-subtle);">
            <div style="font-size:0.75rem; color:var(--text-secondary); text-transform:uppercase; font-weight:700;">Code:</div>
            <code style="font-size:1.05rem; font-weight:700; color:var(--accent-gold); letter-spacing:0.05em;">${store.currentGroup.invite_code}</code>
            <button id="copy-group-invite-btn" class="icon-btn" style="width:28px; height:28px;" title="Copy invite link">
              <i class="fa-regular fa-copy"></i>
            </button>
          </div>
        </div>
      ` : ''}

      <!-- PROGRESS SUMMARY CARDS -->
      <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(200px, 1fr)); gap:1rem; margin-bottom:2.5rem;">
        <div class="stat-card">
          <div class="stat-num" style="color:var(--accent-emerald);">${stats.completedLessons}/${stats.totalLessons}</div>
          <div class="stat-label">Lessons Completed</div>
        </div>
        <div class="stat-card">
          <div class="stat-num" style="color:var(--accent-gold);">${stats.memorizedVerses}/${stats.totalVerses}</div>
          <div class="stat-label">Verses Memorized</div>
        </div>
        <div class="stat-card">
          <div class="stat-num" style="color:var(--accent-blue);">${myReflections.length}</div>
          <div class="stat-label">Journal Entries</div>
        </div>
        <div class="stat-card">
          <div class="stat-num">${stats.percentage}%</div>
          <div class="stat-label">Course Progress</div>
        </div>
      </div>

      <!-- DASHBOARD TABS -->
      <div style="display:flex; gap:0.5rem; border-bottom:1px solid var(--border-subtle); margin-bottom:2rem; flex-wrap:wrap;">
        <button class="nav-link-btn ${activeDashTab === 'reflections' ? 'active' : ''}" data-d-tab="reflections">
          <i class="fa-solid fa-book-journal-whills"></i> My Reflection Journal (${myReflections.length})
        </button>
        <button class="nav-link-btn ${activeDashTab === 'cohort' ? 'active' : ''}" data-d-tab="cohort">
          <i class="fa-solid fa-people-group"></i> ${store.currentGroup ? store.currentGroup.name : 'Cohort'} Discussions (${store.cohortReflections.length})
        </button>
        <button class="nav-link-btn ${activeDashTab === 'members' ? 'active' : ''}" data-d-tab="members">
          <i class="fa-solid fa-users-viewfinder"></i> Group Members (${store.groupMembers.length || (store.currentGroup ? 1 : 0)})
        </button>
        <button class="nav-link-btn ${activeDashTab === 'completed' ? 'active' : ''}" data-d-tab="completed">
          <i class="fa-solid fa-circle-check"></i> Completed Lessons (${stats.completedLessons})
        </button>
      </div>

      <!-- TAB CONTENT -->
      <div id="dash-tab-pane">
        ${renderDashTabPane(activeDashTab, myReflections)}
      </div>

      <!-- MODAL CONTAINERS -->
      <div id="edit-profile-modal-root"></div>
      <div id="group-action-modal-root"></div>
    </div>
  `;

  // Attach tab switchers
  container.querySelectorAll('[data-d-tab]').forEach(btn => {
    btn.onclick = () => {
      activeDashTab = btn.getAttribute('data-d-tab');
      renderDashboard(container);
    };
  });

  // Copy invite link button
  const copyInviteBtn = container.querySelector('#copy-group-invite-btn');
  if (copyInviteBtn && store.currentGroup) {
    copyInviteBtn.onclick = () => {
      const joinUrl = `${window.location.origin}${window.location.pathname}#join/${store.currentGroup.invite_code}`;
      navigator.clipboard.writeText(joinUrl).then(() => {
        showToast(`Invite link copied to clipboard! (Code: ${store.currentGroup.invite_code})`, 'success');
      }).catch(() => {
        showToast(`Group Code: ${store.currentGroup.invite_code}`, 'info');
      });
    };
  }

  // Group Action Button (Manage / Join / Create)
  const groupActionBtn = container.querySelector('#group-action-btn');
  if (groupActionBtn) {
    groupActionBtn.onclick = () => openGroupModal();
  }

  // Edit profile button
  const editBtn = container.querySelector('#edit-profile-btn');
  if (editBtn) {
    editBtn.onclick = () => openEditProfileModal(profile, user);
  }
}

function renderDashTabPane(tab, myReflections) {
  if (tab === 'reflections') {
    if (myReflections.length === 0) {
      return `
        <div style="text-align:center; padding:3rem 1rem; color:var(--text-secondary);">
          <i class="fa-solid fa-pen-nib" style="font-size:2rem; margin-bottom:1rem; opacity:0.5;"></i>
          <p>You haven't saved any reflections yet. Go to any lesson and share your thoughts in the <strong>Feel</strong> tab!</p>
          <a href="#course" class="btn-primary" style="margin:1.5rem auto 0; display:inline-flex;">Browse Lessons</a>
        </div>
      `;
    }

    return `
      <div style="display:flex; flex-direction:column; gap:1.25rem;">
        ${myReflections.map(r => `
          <div class="module-card" style="padding:1.5rem;">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.75rem;">
              <span style="font-weight:700; color:var(--accent-blue); font-size:0.85rem;">
                Lesson ${r.lesson.overall_order}: ${r.lesson.title}
              </span>
              <span style="font-size:0.75rem; color:var(--text-muted);">
                ${r.share_with_cohort ? '<i class="fa-solid fa-users" style="color:var(--accent-emerald);"></i> Shared with Cohort' : '<i class="fa-solid fa-lock"></i> Private'}
              </span>
            </div>
            <p style="font-size:1.05rem; line-height:1.65; white-space:pre-wrap; color:var(--text-primary); margin-bottom:1rem;">${r.response_text}</p>
            <div style="display:flex; justify-content:flex-end;">
              <a href="#lesson/${r.lesson.id}" class="btn-secondary" style="font-size:0.82rem; padding:0.35rem 0.8rem;">
                Open Lesson <i class="fa-solid fa-arrow-right"></i>
              </a>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  if (tab === 'cohort') {
    const list = store.cohortReflections;
    if (list.length === 0) {
      return `
        <div style="text-align:center; padding:3rem 1rem; color:var(--text-secondary);">
          <i class="fa-solid fa-users" style="font-size:2rem; margin-bottom:1rem; opacity:0.5;"></i>
          <p>No cohort reflections shared yet. When members save reflections with "Share with cohort" enabled, they will appear here!</p>
        </div>
      `;
    }

    return `
      <div style="display:flex; flex-direction:column; gap:1.25rem;">
        ${list.map(c => `
          <div class="module-card" style="padding:1.5rem;">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.75rem;">
              <div style="display:flex; align-items:center; gap:0.5rem;">
                <div class="avatar-circle" style="width:28px; height:28px; font-size:0.75rem;">
                  ${(c.profiles?.display_name || 'D').charAt(0).toUpperCase()}
                </div>
                <span style="font-weight:600; font-size:0.9rem;">
                  ${c.profiles?.display_name || 'Fellow Disciple'}
                </span>
                <span style="font-size:0.75rem; color:var(--text-muted);">
                  (${c.profiles?.cohort_name || 'Cohort'})
                </span>
              </div>
            </div>
            <p style="font-size:1rem; line-height:1.6; white-space:pre-wrap; color:var(--text-primary);">${c.response_text}</p>
          </div>
        `).join('')}
      </div>
    `;
  }

  if (tab === 'members') {
    if (!store.currentGroup) {
      return `
        <div style="text-align:center; padding:3rem 1rem; color:var(--text-secondary);">
          <i class="fa-solid fa-users" style="font-size:2rem; margin-bottom:1rem; opacity:0.5;"></i>
          <p>You are not currently in a small group or cohort.</p>
          <button id="tab-join-group-btn" class="btn-primary" style="margin:1.5rem auto 0; display:inline-flex;">
            <i class="fa-solid fa-people-roof"></i> Join or Start a Group
          </button>
        </div>
      `;
    }

    const members = store.groupMembers.length > 0 ? store.groupMembers : [{
      role: store.currentGroup.my_role || 'leader',
      joined_at: new Date().toISOString(),
      profiles: authState.profile
    }];

    return `
      <div>
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.25rem; flex-wrap:wrap; gap:1rem;">
          <div>
            <h3 style="font-family:var(--font-display); font-size:1.3rem; font-weight:700;">
              ${store.currentGroup.name} Roster
            </h3>
            <p style="font-size:0.85rem; color:var(--text-secondary);">
              Group Invite Code: <strong style="color:var(--accent-gold);">${store.currentGroup.invite_code}</strong>
            </p>
          </div>
          <button id="invite-share-btn" class="btn-secondary" style="font-size:0.85rem;">
            <i class="fa-solid fa-share-nodes"></i> Share Invite Link
          </button>
        </div>

        <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:1rem;">
          ${members.map(m => {
            const name = m.profiles?.display_name || m.profiles?.full_name || 'Group Member';
            const isLeader = m.role === 'leader';
            return `
              <div class="module-card" style="padding:1.25rem; display:flex; align-items:center; gap:1rem;">
                <div class="avatar-circle" style="width:48px; height:48px; font-size:1.2rem;">
                  ${name.charAt(0).toUpperCase()}
                </div>
                <div style="flex:1;">
                  <div style="display:flex; align-items:center; gap:0.5rem;">
                    <span style="font-weight:700; font-size:0.95rem;">${name}</span>
                    <span class="badge" style="background:${isLeader ? 'var(--accent-gold)' : 'rgba(255,255,255,0.1)'}; color:${isLeader ? '#000' : 'var(--text-secondary)'}; font-size:0.7rem; padding:0.1rem 0.5rem; border-radius:var(--radius-full); font-weight:700;">
                      ${isLeader ? 'Leader' : 'Member'}
                    </span>
                  </div>
                  <div style="font-size:0.75rem; color:var(--text-muted); margin-top:0.2rem;">
                    Joined ${m.joined_at ? new Date(m.joined_at).toLocaleDateString() : 'Recently'}
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  if (tab === 'completed') {
    const completed = store.lessons.filter(l => store.progress[l.id]?.is_completed);
    if (completed.length === 0) {
      return `
        <div style="text-align:center; padding:3rem 1rem; color:var(--text-secondary);">
          <p>No completed lessons yet. Pick a lesson and mark it completed when you finish!</p>
          <a href="#course" class="btn-primary" style="margin:1.5rem auto 0; display:inline-flex;">Browse Lessons</a>
        </div>
      `;
    }

    return `
      <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(300px, 1fr)); gap:1rem;">
        ${completed.map(l => {
          const modName = l.module_name || (store.modules || []).find(m => m.id === l.module_id)?.title || '';
          return `
          <div class="lesson-item-card is-completed">
            <div>
              <span class="lesson-num">Lesson ${l.overall_order}${modName ? ` • ${modName}` : ''}</span>
              <h4 class="lesson-card-title">${l.title}</h4>
            </div>
            <div class="lesson-card-footer">
              <span style="color:var(--accent-emerald); font-weight:600; font-size:0.85rem;">
                <i class="fa-solid fa-circle-check"></i> Finished
              </span>
              <a href="#lesson/${l.id}" class="btn-secondary" style="padding:0.35rem 0.75rem; font-size:0.8rem;">
                Review
              </a>
            </div>
          </div>
        `;
        }).join('')}
      </div>
    `;
  }

  return '<div></div>';
}

function openEditProfileModal(profile, user) {
  const root = document.getElementById('edit-profile-modal-root');
  if (!root) return;

  root.innerHTML = `
    <div class="modal-backdrop" id="edit-profile-backdrop">
      <div class="modal-card">
        <div class="modal-header">
          <h3 class="modal-title">Edit Profile</h3>
          <button class="modal-close-btn" id="close-profile-modal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <form id="profile-edit-form">
            <div class="form-group">
              <label class="form-label" for="edit-display-name">Display Name (Visible to Cohort)</label>
              <input type="text" id="edit-display-name" class="form-input" value="${profile?.display_name || ''}" required />
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-full-name">Full Name</label>
              <input type="text" id="edit-full-name" class="form-input" value="${profile?.full_name || ''}" />
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-cohort-name">Cohort / Small Group / Church</label>
              <input type="text" id="edit-cohort-name" class="form-input" value="${profile?.cohort_name || 'General Cohort'}" />
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-bio">Short Spiritual Journey Bio / Notes</label>
              <textarea id="edit-bio" class="reflection-textarea" style="min-height:80px;" placeholder="Tell your cohort a little about your spiritual walk...">${profile?.bio || ''}</textarea>
            </div>

            <button type="submit" id="save-profile-btn" class="btn-primary" style="width:100%; justify-content:center; padding:0.8rem;">
              <i class="fa-solid fa-floppy-disk"></i> Save Profile Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  `;

  const backdrop = document.getElementById('edit-profile-backdrop');
  const closeBtn = document.getElementById('close-profile-modal');
  const close = () => { root.innerHTML = ''; };
  backdrop.onclick = (e) => { if (e.target === backdrop) close(); };
  closeBtn.onclick = close;

  const form = document.getElementById('profile-edit-form');
  form.onsubmit = async (e) => {
    e.preventDefault();
    const displayName = document.getElementById('edit-display-name').value.trim();
    const fullName = document.getElementById('edit-full-name').value.trim();
    const cohortName = document.getElementById('edit-cohort-name').value.trim();
    const bio = document.getElementById('edit-bio').value.trim();

    try {
      await updateProfile({
        display_name: displayName,
        full_name: fullName,
        cohort_name: cohortName,
        bio: bio
      });
      showToast('Profile updated!', 'success');
      close();
      renderDashboard(document.getElementById('main-content'));
    } catch (err) {
      showToast('Failed to update profile: ' + err.message, 'error');
    }
  };
}

export function openGroupModal() {
  const root = document.getElementById('group-action-modal-root');
  if (!root) return;

  const currentGroup = store.currentGroup;

  root.innerHTML = `
    <div class="modal-backdrop" id="group-modal-backdrop">
      <div class="modal-card" style="max-width:520px;">
        <div class="modal-header">
          <div style="display:flex; align-items:center; gap:0.5rem;">
            <div class="brand-icon-box" style="width:32px; height:32px; font-size:1rem;">
              <i class="fa-solid fa-people-roof"></i>
            </div>
            <h3 class="modal-title">
              ${currentGroup ? 'Manage Discipleship Group' : 'Join or Create a Group'}
            </h3>
          </div>
          <button class="modal-close-btn" id="close-group-modal" aria-label="Close modal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          ${currentGroup ? `
            <div style="text-align:center; padding:1rem 0 1.5rem; border-bottom:1px solid var(--border-subtle); margin-bottom:1.5rem;">
              <div style="font-size:0.8rem; font-weight:700; color:var(--accent-blue); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.25rem;">
                Active Group
              </div>
              <h2 style="font-family:var(--font-display); font-size:1.6rem; font-weight:700; margin-bottom:0.5rem;">
                ${currentGroup.name}
              </h2>
              <p style="font-size:0.9rem; color:var(--text-secondary); max-width:400px; margin:0 auto 1.25rem;">
                ${currentGroup.description || 'Discipleship cohort studying Walk Together Ministries curriculum.'}
              </p>

              <div style="background:rgba(255,255,255,0.04); border:1px solid var(--border-subtle); border-radius:var(--radius-lg); padding:1rem; display:inline-flex; flex-direction:column; gap:0.5rem; align-items:center;">
                <div style="font-size:0.75rem; color:var(--text-secondary); text-transform:uppercase; font-weight:700;">Shareable Invite Code:</div>
                <div style="font-size:1.6rem; font-weight:800; font-family:var(--font-display); letter-spacing:0.1em; color:var(--accent-gold);">
                  ${currentGroup.invite_code}
                </div>
                <div style="display:flex; gap:0.5rem;">
                  <button id="modal-copy-link-btn" class="btn-primary" style="font-size:0.8rem; padding:0.4rem 0.9rem;">
                    <i class="fa-regular fa-copy"></i> Copy Invite Link
                  </button>
                  <button id="modal-copy-code-btn" class="btn-secondary" style="font-size:0.8rem; padding:0.4rem 0.9rem;">
                    Copy Code Only
                  </button>
                </div>
              </div>
            </div>

            <div style="border-top:1px solid var(--border-subtle); padding-top:1rem;">
              <h4 style="font-size:0.95rem; font-weight:600; margin-bottom:0.75rem;">Switch to another group</h4>
              <form id="switch-group-form" style="display:flex; gap:0.5rem;">
                <input type="text" id="switch-group-code" class="form-input" placeholder="Enter other Invite Code" style="text-transform:uppercase; font-weight:600;" required />
                <button type="submit" class="btn-secondary" style="white-space:nowrap; padding:0 1rem;">
                  Join
                </button>
              </form>
            </div>
          ` : `
            <!-- NO GROUP YET: TAB SELECTOR JOIN VS CREATE -->
            <div style="display:flex; border-bottom:1px solid var(--border-subtle); margin-bottom:1.25rem;">
              <button class="nav-link-btn active" id="modal-tab-join" style="flex:1; justify-content:center; padding:0.6rem;">
                Join Existing Group
              </button>
              <button class="nav-link-btn" id="modal-tab-create" style="flex:1; justify-content:center; padding:0.6rem;">
                Start New Group
              </button>
            </div>

            <div id="modal-join-pane">
              <p style="font-size:0.9rem; color:var(--text-secondary); margin-bottom:1.25rem;">
                If your leader or friend gave you a group code, enter it below to link your discipleship journey.
              </p>
              <form id="join-group-form">
                <div class="form-group">
                  <label class="form-label" for="join-code-input">Group Invite Code</label>
                  <input type="text" id="join-code-input" class="form-input" placeholder="e.g. WALK-A4B2" style="text-transform:uppercase; font-weight:600; letter-spacing:0.05em;" required />
                </div>
                <button type="submit" id="submit-join-group-btn" class="btn-primary" style="width:100%; justify-content:center; padding:0.8rem;">
                  <i class="fa-solid fa-right-to-bracket"></i> Join Group
                </button>
              </form>
            </div>

            <div id="modal-create-pane" style="display:none;">
              <p style="font-size:0.9rem; color:var(--text-secondary); margin-bottom:1.25rem;">
                Create a space for your church small group, family, or friends. You'll receive a code you can send to them.
              </p>
              <form id="create-group-form">
                <div class="form-group">
                  <label class="form-label" for="create-name-input">Group Name</label>
                  <input type="text" id="create-name-input" class="form-input" placeholder="e.g. Austin Men's Fellowship" required />
                </div>
                <div class="form-group">
                  <label class="form-label" for="create-desc-input">Short Description (Optional)</label>
                  <textarea id="create-desc-input" class="reflection-textarea" style="min-height:70px;" placeholder="Meeting times, focus, or church connection..."></textarea>
                </div>
                <button type="submit" id="submit-create-group-btn" class="btn-primary" style="width:100%; justify-content:center; padding:0.8rem;">
                  <i class="fa-solid fa-plus"></i> Create Group & Generate Code
                </button>
              </form>
            </div>
          `}
        </div>
      </div>
    </div>
  `;

  const backdrop = document.getElementById('group-modal-backdrop');
  const closeBtn = document.getElementById('close-group-modal');
  const close = () => { root.innerHTML = ''; };
  backdrop.onclick = (e) => { if (e.target === backdrop) close(); };
  closeBtn.onclick = close;

  // Copy buttons
  const copyLink = document.getElementById('modal-copy-link-btn');
  if (copyLink && currentGroup) {
    copyLink.onclick = () => {
      const joinUrl = `${window.location.origin}${window.location.pathname}#join/${currentGroup.invite_code}`;
      navigator.clipboard.writeText(joinUrl).then(() => {
        showToast('Invite link copied! Share this with your group.', 'success');
      });
    };
  }

  const copyCode = document.getElementById('modal-copy-code-btn');
  if (copyCode && currentGroup) {
    copyCode.onclick = () => {
      navigator.clipboard.writeText(currentGroup.invite_code).then(() => {
        showToast(`Code ${currentGroup.invite_code} copied!`, 'success');
      });
    };
  }

  // Switch / Join form
  const joinForm = document.getElementById('join-group-form') || document.getElementById('switch-group-form');
  if (joinForm) {
    joinForm.onsubmit = async (e) => {
      e.preventDefault();
      const codeInput = document.getElementById('join-code-input') || document.getElementById('switch-group-code');
      const code = codeInput.value.trim();
      try {
        await joinGroup(code);
        showToast('Successfully joined group!', 'success');
        if (authState.user) {
          await fetchUserProgressFromSupabase(authState.user.id);
        }
        close();
        renderDashboard(document.getElementById('main-content'));
      } catch (err) {
        showToast(err.message || 'Could not join group with that code.', 'error');
      }
    };
  }

  // Create form
  const createForm = document.getElementById('create-group-form');
  if (createForm) {
    createForm.onsubmit = async (e) => {
      e.preventDefault();
      const name = document.getElementById('create-name-input').value.trim();
      const desc = document.getElementById('create-desc-input').value.trim();
      try {
        const newG = await createGroup({ name, description: desc });
        showToast(`Group "${newG.name}" created! Invite code: ${newG.invite_code}`, 'success');
        if (authState.user) {
          await fetchUserProgressFromSupabase(authState.user.id);
        }
        close();
        renderDashboard(document.getElementById('main-content'));
      } catch (err) {
        showToast(err.message || 'Failed to create group.', 'error');
      }
    };
  }

  // Tab switcher in join/create modal
  const tabJoin = document.getElementById('modal-tab-join');
  const tabCreate = document.getElementById('modal-tab-create');
  const paneJoin = document.getElementById('modal-join-pane');
  const paneCreate = document.getElementById('modal-create-pane');

  if (tabJoin && tabCreate) {
    tabJoin.onclick = () => {
      tabJoin.classList.add('active');
      tabCreate.classList.remove('active');
      paneJoin.style.display = 'block';
      paneCreate.style.display = 'none';
    };
    tabCreate.onclick = () => {
      tabCreate.classList.add('active');
      tabJoin.classList.remove('active');
      paneJoin.style.display = 'none';
      paneCreate.style.display = 'block';
    };
  }
}

